import React from 'react';

import { PaginationBar } from '../../ComponentsLibrary';
import { clearPreferenceCache } from '../../helpers/uiPreferencesHelpers';
import { handleAPIRequest } from '../Utilities/handleAPIRequest';

import type { Dispatch } from 'react';

import type {
  CursorTablePaginationMetadata,
  TablePaginationAction,
  TPaginationState
} from './types';
import type { Total } from '../../api/cursorPagination';

const ROW_PER_PAGE_OPTIONS = [10, 20, 30, 40, 50];

interface SharedPaginationProps {
  paginationState: TPaginationState<string>;
  dispatch: Dispatch<TablePaginationAction<string>>;
  userPreferenceSource?: string;
}

// The flat-total props every existing caller passes; `mode` is optional so they compile as-is.
type NumberedPaginationProps = SharedPaginationProps & {
  mode?: 'numbered';
  totalPages: number;
  totalItems: number;
  total?: Total;
};

type CursorPaginationProps = SharedPaginationProps & {
  mode: 'cursor';
  metadata: CursorTablePaginationMetadata;
};

export type GenericTablePaginationProps = NumberedPaginationProps | CursorPaginationProps;

const isCursorPagination = (props: GenericTablePaginationProps): props is CursorPaginationProps =>
  props.mode === 'cursor';

const saveRowCountPreference = (userPreferenceSource: string | undefined, pageSize: number) => {
  if (!userPreferenceSource) return;

  handleAPIRequest({
    method: 'POST',
    url: '/api/v4/ui_preferences',
    body: {
      source: userPreferenceSource,
      row_count: pageSize
    }
  });
  clearPreferenceCache(userPreferenceSource);
};

const GenericTablePagination = (props: GenericTablePaginationProps) => {
  const { paginationState, dispatch, userPreferenceSource } = props;

  // Guard against a saved preference that doesn't match the accepted list (e.g. a legacy "5
  // per page" preference from another page). Without this, MUI Select receives a value that
  // has no matching MenuItem and renders blank. On first render we snap to the nearest valid
  // option visually; once the user picks a value from the dropdown the state re-syncs normally.
  const closestRowPerPageOption = ROW_PER_PAGE_OPTIONS.find(
    (option) => option >= (paginationState.limit || 10)
  );

  if (isCursorPagination(props)) {
    const { metadata } = props;

    // One SET_LIMIT: the reducer restarts a cursor traversal itself, so there is no SET_PAGE.
    return (
      <PaginationBar
        isFetching={metadata.isFetching}
        mode="cursor"
        nextCursor={metadata.nextCursor}
        onNavigate={(boundary) => dispatch({ type: 'SET_CURSOR', payload: boundary })}
        pageSize={closestRowPerPageOption ?? 10}
        pageSizeOptions={ROW_PER_PAGE_OPTIONS}
        previousCursor={metadata.previousCursor}
        total={metadata.total}
        onPageSizeChange={(pageSize) => {
          dispatch({ type: 'SET_LIMIT', payload: pageSize });
          saveRowCountPreference(userPreferenceSource, pageSize);
        }}
      />
    );
  }

  const { totalItems, totalPages, total } = props;

  const handleSetLimit = (pageSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: pageSize });
    dispatch({ type: 'SET_PAGE', payload: 1 });
    saveRowCountPreference(userPreferenceSource, pageSize);
  };

  const handleSetPage = (page: number) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  };

  return (
    <PaginationBar
      onPageChange={handleSetPage}
      onPageSizeChange={handleSetLimit}
      page={paginationState.page}
      pageSize={closestRowPerPageOption ?? 10}
      pageSizeOptions={ROW_PER_PAGE_OPTIONS}
      total={total}
      totalItems={totalItems}
      totalPages={totalPages}
    />
  );
};

export default GenericTablePagination;
